import {
  LoanApplicationCaseStatus,
  LoanApplicationCaseStatusLabels,
  LoanApplicationStatus,
  LoanApplicationStatusLabels,
  LoanProductType,
} from "../../../hooks/api/types";
import { LOSPlatform } from "../../../contexts";
import { DateTime } from "luxon";
import { DRAWDOWN_ELIGIBLE_STATUSES, POST_ACTIVE_STATUSES } from "./constants";

type LoanForDateDisplay = {
  loanType?: string;
  creditLine?: { endDate?: string };
  losPlatform?: string;
  caseStatus?: LoanApplicationCaseStatus | null;
  status?: LoanApplicationStatus;
  submittedAt?: string | null;
};

const formatDate = (date: string) =>
  DateTime.fromISO(date).toFormat("dd LLL yyyy");

/**
 * Shared date display for LOC facility: "Valid until", "Closed on", "Due date", or "Applied on".
 */
export const getCreditLineDateDisplay = (
  loan: LoanForDateDisplay
): string | null => {
  const isCreditLine =
    loan.loanType === LoanProductType.LOC_INVOICE_DISCOUNTING ||
    loan.loanType === LoanProductType.LOC_PAYABLE_FINANCING;

  if (isCreditLine && loan.creditLine?.endDate) {
    const displayStatus =
      loan.losPlatform === LOSPlatform.CREDIBLEX
        ? loan.caseStatus != null
          ? LoanApplicationCaseStatusLabels[loan.caseStatus!]?.label
          : undefined
        : loan.status != null
        ? LoanApplicationStatusLabels[loan.status!]?.label
        : undefined;

    if (displayStatus === "Active") {
      return `Valid until ${formatDate(loan.creditLine.endDate)}`;
    }
    if (displayStatus === "Closed") {
      return `Closed on ${formatDate(loan.creditLine.endDate)}`;
    }
    if (displayStatus === "Overdue" || displayStatus === "Late Fee Applied") {
      return `Due date ${formatDate(loan.creditLine.endDate)}`;
    }
  }

  return loan.submittedAt ? `Applied on ${formatDate(loan.submittedAt)}` : null;
};

export const shouldIssueDrawdown = (status: LoanApplicationCaseStatus) => {
  return DRAWDOWN_ELIGIBLE_STATUSES.includes(status);
};

export const isPostActiveStatus = (status: LoanApplicationCaseStatus) => {
  return POST_ACTIVE_STATUSES.includes(status);
};

export const toDateOrUndefined = (value: unknown): Date | undefined => {
  if (!value) return undefined;
  if (value instanceof Date) return value;
  if (typeof value === "string" || typeof value === "number") {
    const d = new Date(value);
    return isNaN(d.getTime()) ? undefined : d;
  }
  return undefined;
};
